import React, { useContext, useEffect, useState } from 'react'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Grid,
  Modal,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import API from '../../../../services/API'
import { GlobalContext } from '../../../../App'
import AnDateField from '../../../../components/DateTime/DateField'
import AnDateTimePicker from '../../../../components/DateTime/DateTimePicker'
import WitnessPicker from '../../../../components/Medicines/WitnessPicker'
import {
  emptyWitness,
  isWitnessComplete,
  toWitnessPayload,
  witnessFromLog,
} from '../../../../helpers/medicineWitness'

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '38rem',
  maxWidth: '90vw',
  bgcolor: 'background.paper',
  borderRadius: 1,
  boxShadow: 24,
  padding: 2,
  outline: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5,
}

// Corrects an administration's recorded details (wrong patient/dose/time) -
// the drug was actually given, so stock is untouched. The original record
// is never edited; this creates a new linked record and flags the original
// as corrected. Changing WHICH medicine/batch/bag was consumed isn't
// possible here on purpose - that's a void + re-record, not a correction.
const CorrectAdministrationModal = ({ administration, onClose, onCorrected }) => {
  const { setToast } = useContext(GlobalContext)
  const [reason, setReason] = useState('')
  const [patientName, setPatientName] = useState('')
  const [patientDOB, setPatientDOB] = useState(null)
  const [totalDose, setTotalDose] = useState('')
  const [totalDoseType, setTotalDoseType] = useState('')
  const [administeredTime, setAdministeredTime] = useState(null)
  const [witness, setWitness] = useState(emptyWitness())
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!administration) return
    setReason('')
    setPatientName(administration.patientName || '')
    setPatientDOB(administration.patientDOB || null)
    setTotalDose(administration.totalDose || '')
    setTotalDoseType(administration.totalDoseType || '')
    setAdministeredTime(administration.administeredTime || null)
    setWitness(witnessFromLog(administration))
  }, [administration])

  const isControlled = !!administration?.medicineDescription?.isControlled
  // Older records hold the witness as a typed name only. The picker can't be
  // prefilled from that, but the server carries the name over unless a new
  // witness is chosen - so a legacy name still satisfies the requirement.
  const legacyWitnessName =
    !administration?.witness?.type && administration?.witnessedBy
      ? administration.witnessedBy
      : ''
  const witnessSatisfied =
    !isControlled || isWitnessComplete(witness) || !!legacyWitnessName

  const handleSubmit = (e) => {
    e.preventDefault()
    setSubmitting(true)
    API.post(`/medsmx/administration/${administration._id}/correct`, {
      reason,
      patientName,
      patientDOB,
      totalDose,
      totalDoseType,
      administeredTime,
      witness: isControlled ? toWitnessPayload(witness) : undefined,
    })
      .then((res) => {
        if (res.status === 200) {
          setToast({ message: 'Correction recorded' })
          onCorrected?.(res.data)
          onClose()
        } else {
          setToast({ message: 'Failed to correct administration', severity: 'error' })
        }
      })
      .catch((e) => {
        console.error(e)
        setToast({ message: 'Failed to correct administration', severity: 'error' })
      })
      .finally(() => setSubmitting(false))
  }

  return (
    <Modal open={!!administration} onClose={onClose}>
      <Box sx={style} component="form" onSubmit={handleSubmit}>
        <Typography variant="title2" fontSize="1.2rem">
          Correct Administration
        </Typography>
        <Alert severity="info" icon={false}>
          <AlertTitle>The drug was given - only the record is wrong</AlertTitle>
          The original stays on file exactly as recorded, marked as
          corrected. This creates a new linked record with the fixed
          details. Stock levels aren&apos;t affected. Need to change which
          medicine, batch, or bag was used? Void this entry instead and
          record it again.
        </Alert>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <TextField
              value={patientName}
              onChange={(e) => setPatientName(e.target.value)}
              label="Patient Name"
              fullWidth
              autoComplete="off"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <AnDateField
              fullWidth
              date={patientDOB}
              onChange={setPatientDOB}
              label="Patient DOB"
              format="DD/MM/YYYY"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              value={totalDose}
              onChange={(e) => setTotalDose(e.target.value)}
              label="Dose"
              fullWidth
              autoComplete="off"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              value={totalDoseType}
              onChange={(e) => setTotalDoseType(e.target.value)}
              label="Dose Unit"
              fullWidth
              autoComplete="off"
            />
          </Grid>
          <Grid item xs={12}>
            <AnDateTimePicker
              fullWidth
              date={administeredTime}
              onChange={setAdministeredTime}
              label="Administered Time"
            />
          </Grid>
          {isControlled && (
            <Grid item xs={12}>
              <Stack gap={1}>
                <Typography variant="subtitle2">
                  Controlled drug witness
                </Typography>
                <WitnessPicker
                  value={witness}
                  onChange={setWitness}
                  preventSelectingSelf={false}
                  excludeUserIds={[administration?.administeredBy?._id]}
                  required={!legacyWitnessName}
                  helperText={
                    legacyWitnessName
                      ? `Currently recorded as "${legacyWitnessName}". Leave blank to keep that, or pick a witness to replace it.`
                      : undefined
                  }
                />
              </Stack>
            </Grid>
          )}
        </Grid>
        <TextField
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          label="Reason for correction"
          required
          autoComplete="off"
          multiline
          minRows={2}
        />
        <Stack direction="row" gap={1} justifyContent="flex-end">
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting || !reason || !witnessSatisfied}
          >
            {submitting ? 'Saving...' : 'Save Correction'}
          </Button>
        </Stack>
      </Box>
    </Modal>
  )
}

export default CorrectAdministrationModal
